import { useEffect, useRef, useState } from "react";
// REDUX
import { useLazyGetDebounceOverridesQuery } from "../../redux/debounceOverrides/debounceOverridesAPI";
// AUTHORIZATION
import GETJwtToken from "../../redux/authentication/authentication";
// INTERFACES
import { DebounceOverrideInterface } from "../../interfaces/DebounceOverride.interface";

/**
 * Fetches per-bank debounce overrides via `useLazyGetDebounceOverridesQuery`.
 *
 * Lazy because the bank set is chosen by the caller rather than known at mount.
 *
 * Resolves a JWT first and passes it to the query, which stays skipped until the
 * token arrives. Scoped to the globally selected organisation unless overridden.
 */
export default function useGetDebounceOverrides(bankIds: string[]) {
  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [debounceOverrides, setDebounceOverrides] = useState<DebounceOverrideInterface[]>([]);
  const [token, setToken] = useState<string | null>(null);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [triggerGetOverrides] = useLazyGetDebounceOverridesQuery();

  // Track previous bankIds to avoid redundant fetches
  const prevBankIdsRef = useRef<string>("");

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    GETJwtToken().then(setToken);
  }, []);

  useEffect(() => {
    if (!token || bankIds.length === 0) return;

    const fingerprint = bankIds.slice().sort().join(",");
    if (fingerprint === prevBankIdsRef.current) return;
    prevBankIdsRef.current = fingerprint;

    const fetchAll = async () => {
      const results: DebounceOverrideInterface[] = [];

      // Fetch serially via promise chaining. RTK Query lazy-query triggers
      // share a single hook instance and `keepUnusedDataFor: 0` purges the
      // cache the moment the last subscriber drops — concurrent triggers
      // with different args race each other and most resolutions arrive
      // with empty data, leaving `results` empty even when every HTTP call
      // returned 200. Chaining via reduce guarantees each unwrap resolves
      // with its own data before the next trigger fires.
      await bankIds.reduce<Promise<void>>(async (prev, bankId) => {
        await prev;
        try {
          const resp = await triggerGetOverrides({ bankId, token }).unwrap();
          if (resp && resp.data) {
            results.push(...resp.data);
          }
        } catch {
          // 401/network errors are non-fatal — bank simply has no overrides
        }
      }, Promise.resolve());

      setDebounceOverrides(results);
    };

    fetchAll();
  }, [token, bankIds, triggerGetOverrides]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return { debounceOverrides };
}
